import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, Save, User, Lock, Eye, EyeOff } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function PortalProfilePage() {
  const navigate = useNavigate();
  const token = localStorage.getItem('portal_token');
  const [form, setForm] = useState({ first_name: '', last_name: '', email: '', phone: '', company_name: '', job_title: '', address: '', city: '', state: '', zip_code: '' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const headers = { Authorization: `Bearer ${token}` };

  // Password change state
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);
  const [showNewPw, setShowNewPw] = useState(false);

  useEffect(() => {
    axios.get(`${API_URL}/api/portal/me`, { headers })
      .then(res => { setForm(res.data); })
      .catch(() => toast.error('Failed to load profile'))
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/portal/me`, form, { headers });
      toast.success('Profile updated');
      localStorage.setItem('portal_client', JSON.stringify({ id: form.id, first_name: form.first_name, last_name: form.last_name, email: form.email, company_name: form.company_name, phone: form.phone }));
    } catch { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  const handleChangePassword = async () => {
    if (newPassword.length < 6) { toast.error('New password must be at least 6 characters'); return; }
    if (newPassword !== confirmPassword) { toast.error('Passwords do not match'); return; }

    setChangingPassword(true);
    try {
      await axios.post(`${API_URL}/api/portal/change-password`, {
        new_password: newPassword
      }, { headers });
      toast.success('Password changed successfully');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to change password');
    } finally {
      setChangingPassword(false);
    }
  };

  const set = (field, value) => setForm({ ...form, [field]: value });

  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-4 border-[#84CC16] border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="max-w-2xl mx-auto">
      <button onClick={() => navigate('/portal')} className="flex items-center gap-2 text-gray-500 hover:text-gray-700 mb-6 text-sm"><ArrowLeft className="w-4 h-4" /> Back to Dashboard</button>

      {/* Profile Info */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-14 h-14 bg-[#FFF3CD] rounded-full flex items-center justify-center"><User className="w-7 h-7 text-[#F59E0B]" /></div>
          <div><h2 className="text-xl font-bold text-[#0A1628]">My Profile</h2><p className="text-sm text-gray-500">Update your personal information</p></div>
        </div>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <Field label="First Name" value={form.first_name} onChange={(v) => set('first_name', v)} />
            <Field label="Last Name" value={form.last_name} onChange={(v) => set('last_name', v)} />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Email" value={form.email} onChange={(v) => set('email', v)} type="email" />
            <Field label="Phone" value={form.phone} onChange={(v) => set('phone', v)} type="tel" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Company" value={form.company_name} onChange={(v) => set('company_name', v)} />
            <Field label="Job Title" value={form.job_title} onChange={(v) => set('job_title', v)} />
          </div>
          <Field label="Address" value={form.address} onChange={(v) => set('address', v)} />
          <div className="grid grid-cols-3 gap-4">
            <Field label="City" value={form.city} onChange={(v) => set('city', v)} />
            <Field label="State" value={form.state} onChange={(v) => set('state', v)} />
            <Field label="Zip Code" value={form.zip_code} onChange={(v) => set('zip_code', v)} />
          </div>
        </div>
        <div className="flex justify-end mt-6 pt-4 border-t">
          <button onClick={handleSave} disabled={saving} className="flex items-center gap-2 px-6 py-2.5 bg-[#84CC16] text-white font-semibold rounded-xl hover:bg-[#65a30d] disabled:opacity-50 transition-colors">
            <Save className="w-4 h-4" /> {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>

      {/* Change Password Section */}
      <div className="bg-white rounded-xl border border-gray-200 p-6 mt-6" data-testid="change-password-section">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-14 h-14 bg-red-50 rounded-full flex items-center justify-center"><Lock className="w-7 h-7 text-red-500" /></div>
          <div><h2 className="text-xl font-bold text-[#0A1628]">Change Password</h2><p className="text-sm text-gray-500">Update your portal login password</p></div>
        </div>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">New Password</label>
            <div className="relative">
              <input
                type={showNewPw ? 'text' : 'password'}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="At least 6 characters"
                className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#84CC16]/30 focus:border-[#84CC16] pr-10"
                data-testid="new-password-input"
              />
              <button type="button" onClick={() => setShowNewPw(!showNewPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                {showNewPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Confirm New Password</label>
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Repeat new password"
              className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#84CC16]/30 focus:border-[#84CC16]"
              data-testid="confirm-password-input"
            />
          </div>
        </div>
        <div className="flex justify-end mt-6 pt-4 border-t">
          <button
            onClick={handleChangePassword}
            disabled={changingPassword || !newPassword || !confirmPassword}
            className="flex items-center gap-2 px-6 py-2.5 bg-red-500 text-white font-semibold rounded-xl hover:bg-red-600 disabled:opacity-50 transition-colors"
            data-testid="change-password-btn"
          >
            <Lock className="w-4 h-4" /> {changingPassword ? 'Changing...' : 'Change Password'}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type }) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      <input type={type || 'text'} value={value || ''} onChange={(e) => onChange(e.target.value)} className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#84CC16]/30 focus:border-[#84CC16]" />
    </div>
  );
}
